import React, { useMemo } from 'react';
import { useWindowDimensions } from 'react-native';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  ButtonText,
  CircleIcon,
  FormControl,
  FormControlError,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
  HStack,
  Radio,
  RadioGroup,
  RadioIcon,
  RadioIndicator,
  RadioLabel,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { useGetModuleUpgradeOfferPreview } from '@/common/api/endpoints/module-workflow/module-workflow';
import FormField from '@/common/components/forms/FormField';
import { getNextMinorVersion } from '../utils/moduleVersionNumbers';
import {
  ModuleUpgradeOfferPreviewPanel,
  PREVIEW_SCALE,
  STUDENT_DIALOG_MAX_WIDTH,
} from './ModuleUpgradeOfferPreviewPanel';

export type ModuleApprovalType = 'replace_version' | 'new_version' | 'offer_upgrade';

interface ApprovalTypeChoice {
  value: ModuleApprovalType;
  title: string;
  subtitle: string;
}

interface ModuleApprovalRequestFieldsProps {
  moduleId: string;
  sourceVersion: string;
  /** Working draft label in the editor (e.g. 3.0 while based on 2.1). */
  draftVersion: string;
  mayRequestAsNewVersion: boolean;
}

const REQUEST_APPROVAL_NS = 'lecturer.moduleEditor.moduleEditor.dialogs.requestApprovalOptions';

/** Scaled student dialog plus the preview panel's own padding. */
const PREVIEW_COLUMN_WIDTH = Math.round(STUDENT_DIALOG_MAX_WIDTH * PREVIEW_SCALE) + 32;
/** Below this the xl dialog cannot hold the form beside the preview column. */
const SIDE_BY_SIDE_MIN_WINDOW_WIDTH = 840;

type VersionLabelParams = {
  sourceVersion: string;
  draftLabel: string;
  publishVersion: string;
  replaceVersion: string;
};

/**
 * Form fields for the module version approval request dialog.
 * Approval type is chosen via radio; offer-specific fields appear only for offer_upgrade.
 */
export const ModuleApprovalRequestFields: React.FC<ModuleApprovalRequestFieldsProps> = ({
  moduleId,
  sourceVersion,
  draftVersion,
  mayRequestAsNewVersion,
}) => {
  const { t } = useTranslation();
  const { width: windowWidth } = useWindowDimensions();
  const sideBySide = windowWidth >= SIDE_BY_SIDE_MIN_WINDOW_WIDTH;

  const versionLabels = useMemo((): VersionLabelParams => ({
    sourceVersion: String(sourceVersion),
    draftLabel: String(draftVersion),
    publishVersion: String(draftVersion),
    replaceVersion: getNextMinorVersion(sourceVersion),
  }), [draftVersion, sourceVersion]);

  const approvalType = useWatch({ name: 'approvalType' }) as ModuleApprovalType | undefined;
  const offerStudentNote = useWatch({ name: 'offerStudentNote' }) as string | undefined;
  const showOfferPreview = approvalType === 'offer_upgrade';

  const previewQuery = useGetModuleUpgradeOfferPreview(
    moduleId,
    draftVersion,
    undefined,
    {
      query: {
        enabled: showOfferPreview && !!moduleId && !!draftVersion,
      },
    },
  );

  const previewGroups = previewQuery.data?.data?.data?.groups ?? [];

  const intro = t(`${REQUEST_APPROVAL_NS}.intro`, {
    ...versionLabels,
    defaultValue:
      `Draft ${versionLabels.draftLabel} is based on version ${versionLabels.sourceVersion}. `
      + 'Choose how it goes live after approval.',
  });

  const approvalChoices = useMemo((): ApprovalTypeChoice[] => {
    const choices: ApprovalTypeChoice[] = [
      {
        value: 'replace_version',
        title: t(`${REQUEST_APPROVAL_NS}.optionReplaceVersionTitle`, {
          ...versionLabels,
          defaultValue: `Replace ${versionLabels.sourceVersion} with ${versionLabels.replaceVersion}`,
        }),
        subtitle: t(`${REQUEST_APPROVAL_NS}.optionReplaceVersionSubtitle`, {
          ...versionLabels,
          defaultValue:
            `Everyone on ${versionLabels.sourceVersion} moves to ${versionLabels.replaceVersion} automatically. `
            + `The draft ${versionLabels.draftLabel} is renumbered to ${versionLabels.replaceVersion} on approval.`,
        }),
      },
    ];
    if (mayRequestAsNewVersion) {
      choices.push(
        {
          value: 'new_version',
          title: t(`${REQUEST_APPROVAL_NS}.optionNewVersionTitle`, {
            ...versionLabels,
            defaultValue: `Publish new version ${versionLabels.publishVersion}`,
          }),
          subtitle: t(`${REQUEST_APPROVAL_NS}.optionNewVersionSubtitle`, {
            ...versionLabels,
            defaultValue:
              `Adds version ${versionLabels.publishVersion} alongside older versions. `
              + `New students get ${versionLabels.publishVersion}; students on ${versionLabels.sourceVersion} `
              + 'or older keep their current version.',
          }),
        },
        {
          value: 'offer_upgrade',
          title: t(`${REQUEST_APPROVAL_NS}.optionOfferUpgradeTitle`, {
            ...versionLabels,
            defaultValue: `Publish ${versionLabels.publishVersion} with optional upgrade`,
          }),
          subtitle: t(`${REQUEST_APPROVAL_NS}.optionOfferUpgradeSubtitle`, {
            ...versionLabels,
            defaultValue:
              `Students on an older version can opt in to ${versionLabels.publishVersion}. `
              + 'No one is moved automatically.',
          }),
        },
      );
    }
    return choices;
  }, [mayRequestAsNewVersion, t, versionLabels]);

  const formColumn = (
    <VStack space="md" flex={1} minWidth={280}>
      <Text size="sm" color="$textSubtle" lineHeight="$sm">
        {intro}
      </Text>

      <FormControl>
        <FormControlLabel mb="$2">
          <FormControlLabelText size="sm" fontWeight="$semibold" color="$textHeading">
            {t(`${REQUEST_APPROVAL_NS}.approvalTypeLabel`, {
              defaultValue: 'How should this version be published?',
            })}
          </FormControlLabelText>
        </FormControlLabel>
        <Controller
          name="approvalType"
          rules={{
            required: t(`${REQUEST_APPROVAL_NS}.approvalTypeRequired`, {
              defaultValue: 'Please choose how to publish this version',
            }),
          }}
          render={({ field, fieldState }) => (
            <>
              <RadioGroup
                value={field.value ?? ''}
                onChange={field.onChange}
              >
                <VStack space="sm">
                  {approvalChoices.map((option) => (
                    <Radio
                      key={option.value}
                      value={option.value}
                      size="sm"
                      width="$full"
                      alignItems="flex-start"
                      aria-label={option.title}
                    >
                      <HStack space="sm" alignItems="flex-start" flex={1} width="$full">
                        <RadioIndicator
                          mt="$0.5"
                          mr="$0"
                          w="$3.5"
                          h="$3.5"
                          flexShrink={0}
                        >
                          <RadioIcon as={CircleIcon} size="xs" />
                        </RadioIndicator>
                        <VStack flex={1} space="xs" pb="$0.5">
                          <RadioLabel
                            fontSize="$sm"
                            fontWeight="$semibold"
                            color="$textHeading"
                            lineHeight="$sm"
                            mb="$0"
                          >
                            {option.title}
                          </RadioLabel>
                          <Text size="xs" color="$textSubtle" lineHeight="$xs">
                            {option.subtitle}
                          </Text>
                        </VStack>
                      </HStack>
                    </Radio>
                  ))}
                </VStack>
              </RadioGroup>
              {fieldState.error ? (
                <FormControlError>
                  <FormControlErrorText>{fieldState.error.message}</FormControlErrorText>
                </FormControlError>
              ) : null}
            </>
          )}
        />
      </FormControl>

      <FormField
        name="changelogComment"
        label={t(`${REQUEST_APPROVAL_NS}.changelogLabel`, {
          defaultValue: 'Changelog comment (internal only)',
        })}
        type="text"
        placeholder={t(`${REQUEST_APPROVAL_NS}.changelogPlaceholder`, {
          defaultValue: 'Brief description of changes in this version',
        })}
        rules={{
          required: t(`${REQUEST_APPROVAL_NS}.changelogRequired`, {
            defaultValue: 'Please provide a changelog comment',
          }),
        }}
      />

      {showOfferPreview ? (
        <FormField
          name="offerStudentNote"
          label={t(`${REQUEST_APPROVAL_NS}.offerStudentNoteLabel`, {
            defaultValue: 'Note to students (optional)',
          })}
          type="textarea"
          fieldHeight={96}
          placeholder={t(`${REQUEST_APPROVAL_NS}.offerStudentNotePlaceholder`, {
            defaultValue: 'Why you made this change — students see it as "Note from your lecturer"',
          })}
        />
      ) : null}
    </VStack>
  );

  const previewState = showOfferPreview ? (
    previewQuery.isError ? (
      <VStack space="sm" testID="module-upgrade-offer-preview-error">
        <Text size="sm" color="$textBody">
          {t(`${REQUEST_APPROVAL_NS}.previewUnavailable`, {
            defaultValue: 'Preview unavailable. Students will still see the computed changes.',
          })}
        </Text>
        <Button
          size="sm"
          variant="outline"
          alignSelf="flex-start"
          onPress={() => previewQuery.refetch()}
          isDisabled={previewQuery.isFetching}
          testID="module-upgrade-offer-preview-retry"
        >
          <ButtonText>
            {t(`${REQUEST_APPROVAL_NS}.previewRetry`, { defaultValue: 'Retry' })}
          </ButtonText>
        </Button>
      </VStack>
    ) : previewQuery.isLoading ? (
      <Box testID="module-upgrade-offer-preview-loading">
        <Text size="sm" color="$textSubtle">
          {t(`${REQUEST_APPROVAL_NS}.previewLoading`, { defaultValue: 'Loading student preview…' })}
        </Text>
      </Box>
    ) : previewGroups.length === 0 ? (
      <Box testID="module-upgrade-offer-preview-empty">
        <Text size="sm" color="$textSubtle">
          {t(`${REQUEST_APPROVAL_NS}.previewEmpty`, {
            defaultValue: 'No older published version to preview.',
          })}
        </Text>
      </Box>
    ) : (
      <ModuleUpgradeOfferPreviewPanel
        draftVersion={draftVersion}
        groups={previewGroups}
        impactNotice={offerStudentNote ?? ''}
      />
    )
  ) : null;

  const previewColumn = previewState ? (
    <Box
      w={sideBySide ? PREVIEW_COLUMN_WIDTH : '$full'}
      flexShrink={0}
      testID="module-upgrade-offer-preview-column"
    >
      {previewState}
    </Box>
  ) : null;

  return (
    <Box mt="$3" mb="$2" w="$full">
      {sideBySide && showOfferPreview ? (
        <HStack space="2xl" alignItems="flex-start" w="$full">
          {formColumn}
          {previewColumn}
        </HStack>
      ) : (
        <VStack space="lg" w="$full">
          {formColumn}
          {previewColumn}
        </VStack>
      )}
    </Box>
  );
};

export default ModuleApprovalRequestFields;
